import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { colorToArray, darkenMainColorByPercent, ensureBoolean, i18nIntl, isDesktop, isNotEmpty } from '../../lib/ep.js';
import transferPlaceholder from '../../hoc/transferPlaceholder';
import { v4 as uuidv4 } from 'uuid';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import CheckEnabler from '../builder/CheckEnabler.js';
import ColorPicker from '../builder/ColorPicker.js';
import { DeleteButton } from '../builder/ConfirmButton.js';
import FieldLabel from '../builder/FieldLabel.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input.js';
import Message, { InformationMessage } from '../builder/Message.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import RichEditor from '../builder/RichEditor.js';
import Range from '../builder/Range.js';
import SaveButton from '../builder/SaveButton.js';
import Select from '../builder/Select.js';

import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import { StageScoreRecalculate } from './StagesBuilder.js';

import GuessScoreClient, { getAvailableIntervals, getFilteredMatches, getTeamName } from '../front/GuessScoreClient.js';
import OneTimeBracketClient from '../front/OneTimeBracketClient.js';
import Layout from '../front/Layout.js';

import { SingleTeam, TeamsList, TeamsSelector } from '../../containers/StageTeamsManagement.js';
import RibbonCollection from '../ui_items/RibbonCollection.js';
import ToggleButton from '../builder/ToggleButton.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import Tooltip from '../builder/Tooltip.js';
import { isMultiStage } from '../../data/bootstrap_stages.js';
import ActionLink from '../builder/ActionLink.js';
import { translateAction, TranslationLayer } from '../builder/DynamicFields.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const TeamSlot = ({ team, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_hover, setIsHover ] = useState(false);

	const team_index = props?.team_index ?? '0';
	const item_id = `drop_${props.match_index}_${team_index}`;

	const can_delete = (props.onDelete != undefined) ? true : false;
	const type = props?.type ?? 'edit';
	const is_bracket = props?.is_bracket ?? false;
	const blocked_round = props?.blocked_round ?? false;
	const linked_match = props?.linked_match ?? false;

	const default_props = {
		id:item_id,
		onDrop:(type == 'edit') ? (e) => props.onDrop(e, props.match_index, team_index) : null,
	};

	const dragEnter = (e) => {
		e.preventDefault();
		setIsHover(true);
	};

	const dragOver = (e) => {
		e.preventDefault();
		setIsHover(false);
	};

	useEffect(() => {
		if(type != 'edit') return;
		if(is_bracket && blocked_round) return;

		const dropElement = document.getElementById(item_id);

		// NATIVE JS DRAG'N'DROP DOCUMENTATION:
		// https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations

		dropElement.addEventListener('dragenter', (e) => dragEnter(e));
		dropElement.addEventListener('dragover', (e) => dragOver(e));

		return () => {
			dropElement.removeEventListener('dragenter', dragEnter);
			dropElement.removeEventListener('dragover', dragOver);
		}
	}, []);

	const classes = ['slot', `is_${type}`, `is_${(team_index == '1') ? 'visitor' : 'local'}`];
	if(is_hover) classes.join('is_hover');

	if(is_bracket && blocked_round) {
		classes.push('empty blocked_match');

		return (
			<span {...default_props} class={classes.join(" ")}>
				{(linked_match !== false) ? (
					<p><span>[ {linked_match} ]</span></p>
				) : (
					<p><span>[ {i18n("Team will be added automatically")} ]</span></p>
				)}
			</span>
		)
	}

	if(team == undefined) {
		classes.push('empty');

		return (
			<span {...default_props} class={classes.join(" ")}>
				<p><span>[ {i18n("Add a team")} ]</span></p>
			</span>
		)
	}

	return (
		<span {...default_props} class={classes.join(" ")}>
			<SingleTeam
				team={team}
				image_size='small'
				promotion_id={props.promotion_id}
			/>

			{(can_delete) && (
				<SquaredButton
					icon='knk-off'
					color='red'
					onClick={() => props.onDelete(props.match_index, team_index)}
				/>
			)}

			{props.children}
		</span>
	)
};

const GameMatches = ({ stage, is_bracket, change, teams, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_match = {
		type:'match',
		id_1:'',
		id_2:'',
		description:"",
		x2:false,
	};

	const default_round = {
		type:'round',
		title:"",
		x2:false,
	};

	const matches_results = stage?.guess_score_matches_result ?? {};

	const [ matches, setMatches ] = useState(stage?.guess_score_matches ?? []);
	const [ rounds, setRounds ] = useState(stage?.guess_score_rounds ?? []);
	const [ hide_used, setHideUsed ] = useState(is_bracket);
	const [ highlighted_match, setHighlightedMatch ] = useState(false);
	const [ display_match_options, setDisplayMatchOptions ] = useState(false);
	const [ display_round_options, setDisplayRoundOptions ] = useState(false);
	const [ editing_round, setEditingRound ] = useState(0);
	const [ translate_rounds, setTranslateRounds ] = useState(false);

	const promotion = props.promotion;
	const start_date = (isMultiStage(promotion)) ? stage.start_date : promotion.start_entry;
	const started_stage = EPDateAfter(null, start_date) && promotion.status == 1;

	const generateEmptyRounds = (num_rounds) => {
		let round_fields = [ ...rounds ];
		if(num_rounds > rounds.length) {
			[...Array(num_rounds - rounds.length).keys()].map((round) => {
				round_fields.unshift({
					...default_round,
					id:uuidv4(),
				});
			});

		} else {
			round_fields = rounds.slice(rounds.length - num_rounds, rounds.length);
		}

		setRounds(round_fields);
		change('guess_score_rounds', round_fields);
	};

	const changeStage = (k, v) => {
		if(k == 'guess_score_num_rounds') {
			const num_matches = Math.pow(2, v) - 1;
			change('guess_score_num_matches', num_matches);

			generateEmptyRounds(v);
			ensureFilledMatches([ ...matches ], num_matches);
		}

		change(k, v);
	};

	const fillUsedTeams = (_matches) => {
		const used = [];
		_matches.map((match) => {
			if(isNotEmpty(match, 'id_1') && !used.includes(match.id_1)) used.push(match.id_1);
			if(isNotEmpty(match, 'id_2') && !used.includes(match.id_2)) used.push(match.id_2);
		});
		return used;
	};

	const ensureFilledMatches = (_matches, _num_matches = false) => {
		const num_matches = Math.max(1, (_num_matches !== false) ? _num_matches : parseInt(stage?.guess_score_num_matches ?? 1));

		const pending_matches = num_matches - _matches.length;
		if(pending_matches < 0) {
			// remove some matches:
			setMatches(_matches.slice(0, num_matches));
			return;
		}

		// add some empty matches:
		[...Array(pending_matches).keys()].map((foo, m) => {
			_matches.push({ ...default_match, id:uuidv4() });
		});

		setMatches(_matches);
	};

	useEffect(() => {
		ensureFilledMatches([ ...matches ]);
	}, [ stage.guess_score_num_matches ]);

	useEffect(() => {
		change('guess_score_matches', matches);
	}, [ matches ]);

	useEffect(() => {
		if(is_bracket === false) return;
		if(rounds.length > 0) return;

		generateEmptyRounds(4);
	}, []);

	const findTeamById = (id) => {
		return teams.find((t) => (t.id == id));
	};

	const deleteTeamFromMatch = (match_index, team_index) => {
		const _matches = [ ...matches ];
		_matches[match_index][`id_${team_index + 1}`] = '';
		setMatches(_matches);
	};

	const validatePairings = (_matches) => {
		setHighlightedMatch(false);

		const matches_unique_keys = [];
		let repeated_match = false;

		const wrong_matches = _matches.filter((match, m) => {
			if(match.id_1 == '') return false;

			const unique_key = `${match.id_1}_${match.id_2}`;

			if(matches_unique_keys.includes(unique_key)) {
				repeated_match = matches_unique_keys.indexOf(unique_key);
			} else {
				matches_unique_keys.push(unique_key);
			}

			return (match.id_1 == match.id_2) ? true : false;
		});

		if(wrong_matches.length > 0) {
			return {
				status:false,
				message:i18n("A team can't play against itself in a match."),
			};
		}

		if(repeated_match !== false) {
			setTimeout(() => {
				setHighlightedMatch(repeated_match);
			}, 0);

			return {
				status:false,
				message:i18n("This match has already been set."),
			};
		}

		return {
			status:true,
		};
	};

	const deleteAllMatches = (e, cb) => {
		setMatches([]);
		ensureFilledMatches([]);
		if(cb != undefined) cb();
	};

	const onDrop = (e, match_index, team_index) => {
		e.preventDefault();
		const new_team_id = e.dataTransfer.getData('text/plain');

		replaceTeamInMatch(new_team_id, match_index, team_index);
	};

	const replaceTeamInMatch = (new_team_id, match_index, team_index) => {
		const total_rounds = (is_bracket) ? parseInt(stage?.guess_score_num_rounds ?? '4') : 1;

		// in bracket mode, we can't move teams to any slot from rounds bigger than 1
		if(is_bracket && match_index >= Math.pow(2, (total_rounds - 1))) return;

		const _matches = [ ...matches ];
		_matches[match_index][`id_${team_index + 1}`] = new_team_id;

		const validation = validatePairings(_matches);

		if(validation.status === false) {
			alert(validation.message);
			_matches[match_index][`id_${team_index + 1}`] = '';
		}

		setMatches(_matches);
	};

	const promoChange = (k, v, cb) => {
		props.promoChangeSave(k, v);
		if(cb != undefined) cb();
	};

	const categories = props.promotion?.guess_score_categories ?? [];
	const addCategory = (category) => {
		const _categories = [ ...categories ];
		_categories.push({ ...category });
		promoChange('guess_score_categories', _categories);
	};

	const findFirstEmptySlot = () => {
		let empty_match = {
			found:false,
			match_index:0,
			team_index:1,
		};

		matches.map((match, m) => {
			if(empty_match.found !== false) return;

			if(match.id_1 == '') {
				empty_match = {
					found:true,
					match_index:m,
					team_index:0,
				};
				return;
			}

			if(match.id_2 == '') {
				empty_match = {
					found:true,
					match_index:m,
					team_index:1,
				};
				return;
			}
		});

		return empty_match;
	};

	const addToFirstEmptySlot = (team) => {
		const { found, match_index, team_index } = findFirstEmptySlot();
		if(found === false) return;

		replaceTeamInMatch(team.id, match_index, team_index);
	};

	const saveMatchOptions = (e, cb) => {
		setMatches((matches) =>
			matches.map((m) =>
				(m.id != display_match_options.id) ? m : { ...display_match_options }
			)
		);

		setTimeout(() => {
			props.saveStage();
		}, 0);

		if(cb != undefined) cb();
	};

	const saveRoundOptions = (e, cb) => {
		const _rounds = JSON.parse(JSON.stringify(stage.guess_score_rounds));

		const changed_rounds = [];
		_rounds.map((round) => {
			if(round.id == display_round_options.id) {
				changed_rounds.push({ ...display_round_options });
				return;
			}

			changed_rounds.push({ ...round });
		});

		setRounds(changed_rounds);
		change('guess_score_rounds', changed_rounds);

		setTimeout(() => {
			props.saveStage();
		}, 0);

		setDisplayRoundOptions(false);
		if(cb != undefined) cb();
	};

	const translateDispatch = (action) => {
		if(action.type == 'TOGGLE_TRANSLATE') {
			setTranslateRounds(false);
			return;
		}

		const res = translateAction(stage.guess_score_rounds, action);
		change('guess_score_rounds', res);
	};

	const toggleX2 = (match_id, cb) => {
		setMatches((matches) =>
			matches.map((m) =>
				(m.id != match_id) ? m : { ...m, x2:(m.x2 == 'true' ? false : true) }
			)
		);

		setTimeout(() => {
			props.saveStage();
		}, 0);

		if(cb != undefined) cb();
	};

	const filled_matches = matches.filter((m) => (m.id_1 != '' && m.id_2 != ''));

	const grouped_matches = [];
	if(is_bracket === false) {
		grouped_matches.push({
			label:false,
			matches:[...Array(matches.length).keys()],
		});
	} else {
		let previous_index = 0;
		const total_rounds = (is_bracket) ? parseInt(stage?.guess_score_num_rounds ?? '4') : 1;
		[...Array(total_rounds).keys()].map((round, r) => {
			const num_matches_in_round = Math.pow(2, (total_rounds - r - 1));

			const matches_in_round = [];
			for(let i = 0; i < num_matches_in_round; i++) {
				matches_in_round.push(i + previous_index);
			}

			grouped_matches.push({
				label:`${i18n("Round", 'uppercase')} ${r + 1}${(isNotEmpty(rounds[r], 'title') ? `: ${rounds[r].title}` : '')}`,
				matches:matches_in_round,
			});
			previous_index += num_matches_in_round;
		});
	}

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article class="gui_matches_generator">
					<Model model={stage} change={changeStage}>
						{(is_bracket === false) ? (
							<Range
								min={1}
								max={30}
								step={1}
								name="guess_score_num_matches"
								value={parseInt(stage?.guess_score_num_matches ?? '1')}
								disabled={(started_stage) ? true : false}
								disabled_tooltip="You can't modify this option because the stage has already started."
								_class={{ size:60, first:true }}
							>Number of matches</Range>
						) : (
							<Range
								min={2}
								max={4}
								step={1}
								name="guess_score_num_rounds"
								value={parseInt(stage?.guess_score_num_rounds ?? '4')}
								disabled={(started_stage) ? true : false}
								disabled_tooltip="You can't modify this option because the stage has already started."
								_class={{ size:60, first:true }}
							>Number of rounds</Range>
						)}
					</Model>

					{(started_stage) && (
						<Message>
							<p>{i18n("You can't modify the matches because the stage has already started")}</p>
						</Message>
					)}

					{(started_stage === false) && (
						<>
							{(teams.length > 0) && (
								<Message>
									<p>{i18n("Drag and drop teams into slots to create matches.")}</p>
								</Message>
							)}

							<TeamsList
								teams={teams}
								layout='list'
								can_drag={true}
								onClick={addToFirstEmptySlot}
								display_filter={true}
								display_count={true}
								hide_used={hide_used}
								used_teams={fillUsedTeams(matches)}
								image_size='small'
								categories={categories}
								display_empty_categories={false}
								intl={props.intl}
							>
								<TeamsSelector
									selected_teams={teams}
									promotion_id={props.promotion.id}
									promoChange={promoChange}
									addCategory={addCategory}
									addTeams={props.setTeams}
									categories={categories}
									intl={props.intl}
									children_if_empty={<ButtonCollection
										_class={{ other:'gui_not_in_top' }}
									>
										<ActionButton
											icon='knk-add-item'
										>Add teams</ActionButton>
									</ButtonCollection>}
								>
									<ActionButton>Add teams</ActionButton>
								</TeamsSelector>
							</TeamsList>

							{(teams.length > 0 && !is_bracket) && (
								<Checkbox
									name="hide_used"
									value={hide_used}
									change={() => setHideUsed(!hide_used)}
								>Hide teams in use</Checkbox>
							)}
						</>
					)}
				</article>
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				not_awesome={true}
				not_bordered={true}
				off_content={(started_stage === false || (grouped_matches.length > 1 && avail_locales.length > 0)) ? <ButtonCollection>
					{(started_stage === false) && (
						<DeleteButton
							onClick={(e, cb) => deleteAllMatches(cb)}
							disabled={(filled_matches.length == 0) ? true : false}
							_position='right'
						>Delete all</DeleteButton>
					)}

					{(grouped_matches.length > 1 && avail_locales.length > 0) && (
						<SecondaryButton
							icon='knk-translate'
							onClick={() => setTranslateRounds(true)}
						>Translate</SecondaryButton>
					)}
				</ButtonCollection> : null}
			>
				<article class="matches">
					{grouped_matches.map((round, r) => {
						return (
							<div key={`round_${r}`}>
								{(isNotEmpty(round, 'label')) && (
									<div class="round_label">
										<span class="t">{round.label}</span>

										<span>(<ActionLink
											onClick={() => {
												setDisplayRoundOptions(rounds[r]);
												setEditingRound(r + 1);
											}}
										>Edit</ActionLink>)</span>
									</div>
								)}

								<div class="matches_list">
									{round.matches.map((_match, m) => {
										let match = matches[_match];
										if(match == undefined) match = {};

										const team_1 = findTeamById(match.id_1);
										const team_2 = findTeamById(match.id_2);
				
										const highlighted = (highlighted_match !== false && highlighted_match == m) ? true : false;
										const default_props = {
											match_index:m,
											promotion_id:props.promotion_id,
											is_bracket,
											blocked_round:(is_bracket && r > 0) ? true : false,
											intl:props.intl,
										};
				
										if(matches_results[match.id] == undefined && started_stage === false && (!is_bracket || r < 1)) {
											default_props.onDelete = deleteTeamFromMatch;
											default_props.onDrop = onDrop;
										}

										const classes = ['match_pairing_c'];
										if(highlighted) classes.push('is_highlight');
				
										return (
											<div key={`match_pair_${match.id}`} class={classes.join(" ")}>
												<span>{i18n("Match")} #{m + 1}:</span>
				
												<div class="match_pairing">
													<TeamSlot
														{...default_props}
														team={team_1}
														team_index={0}
														linked_match={(is_bracket && r > 0) ? props.intl.formatMessage({
															id:"Winner of the round {round}, match #{match}",
														}, {
															round:r,
															match:2 * m + 1,
														}) : null}
													/>
													
													<span class="vs">{i18n("vs")}</span>
													
													<TeamSlot
														{...default_props}
														team={team_2}
														team_index={1}
														linked_match={(is_bracket && r > 0) ? props.intl.formatMessage({
															id:"Winner of the round {round}, match #{match}",
														}, {
															round:r,
															match:2 * m + 2,
														}) : null}
													/>
				
													{(team_1 != undefined && team_2 != undefined || (is_bracket && r > 0)) ? (
														<ButtonCollection
															_class={{ other:'match_options' }}
														>
															<ToggleButton
																squared={true}
																active={(match?.x2 ?? false) == 'true'}
																icon='knk-x2-on mainColorButton whiteIcon'
																icon_off='knk-x2-off'
																onClick={(e, cb) => toggleX2(match.id, cb)}
																tooltip={<Tooltip
																	with_icon={false}
																	position='top'
																>Selecting this option makes the match worth double points.</Tooltip>}
															/>

															{(is_bracket === false) && (
																<SquaredButton
																	icon='knk-edit'
																	onClick={() => setDisplayMatchOptions(match)}
																/>
															)}
														</ButtonCollection>
													) : (
														<ButtonCollection
															_class={{ oter:'gui_remove_pointer_events match_options' }}
															style={{ opacity:'0' }}
														>
															<SquaredButton
																icon='knk-white-space'
															/>

															{(is_bracket === false) && (
																<SquaredButton
																	icon='knk-white-space'
																/>
															)}
														</ButtonCollection>
													)}
												</div>
											</div>
										)
									})}
								</div>
							</div>
						)
					})}
				</article>

				{(translate_rounds) && (
					<TranslationLayer
						open="all"
						form={stage.guess_score_rounds}
						locales={avail_locales}
						field_types={[{
							type:'round',
							translate:[
								{ key:'title', label:"Title", type:'input' },
							],
							title:"",
						}]}
						dispatch={translateDispatch}
						saveTranslations={(e, cb) => {
							props.saveStage(() => {
								cb();
								setTranslateRounds(false);
							});
						}}
						intl={props.intl}
					/>
				)}

				{(display_match_options) && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setDisplayMatchOptions(false)}
					>
						<div>
							<SectionTitle>Match settings</SectionTitle>

							<SectionSubtitle
								autotranslate={false}
							><strong>{getTeamName(findTeamById(display_match_options.id_1))}</strong> vs <strong>{getTeamName(findTeamById(display_match_options.id_2))}</strong></SectionSubtitle>

							<Model
								model={display_match_options}
								change={(k, v) => setDisplayMatchOptions({ ...display_match_options, [k]:v })}
							>
								<RichEditor
									name='description'
								>Match description</RichEditor>
							</Model>

							<ButtonCollection
								in_floating={true}
							>
								<SaveButton
									onClick={saveMatchOptions}
								>Save changes</SaveButton>

								<SecondaryButton
									onClick={() => setDisplayMatchOptions(false)}
								>Cancel</SecondaryButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}

				{(display_round_options) && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setDisplayRoundOptions(false)}
					>
						<div>
							<SectionTitle>Round settings</SectionTitle>

							<SectionSubtitle
								autotranslate={false}
							><strong>{i18n("Round")} #{editing_round}</strong></SectionSubtitle>

							<Model
								model={display_round_options}
								change={(k, v) => setDisplayRoundOptions({ ...display_round_options, [k]:v })}
							>
								<Input
									name='title'
									_class={{ size:80 }}
								>Title</Input>
							</Model>

							<ButtonCollection
								in_floating={true}
							>
								<SaveButton
									onClick={saveRoundOptions}
								>Save changes</SaveButton>

								<SecondaryButton
									onClick={() => setDisplayRoundOptions(false)}
								>Cancel</SecondaryButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, is_bracket, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type = stage?.guess_score_type ?? 'result';

	const step_interval = parseInt(stage?.guess_score_step_interval ?? '2');
	const max_interval = parseInt(stage?.guess_score_max_interval ?? '3');

	const defined_intervals = getAvailableIntervals(step_interval, max_interval, false);

	const default_description_text = `<h1>${i18n("Predict the results")}</h1><p>${i18n("Make your prediction for each match.")}</p>`;

	const default_bg_color = props.promotion?.layout_props?.color?.background_color ?? 'rgba(255, 255, 255, 1)';
	const default_text_color = props.promotion?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const default_main_color = props.promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';

	const open_stage = EPDateAfter(null, stage.start_date);

	const x2_matches = (stage?.guess_score_matches ?? []).filter((m) => (ensureBoolean(m?.x2 ?? false)));

	const ClientComponent = (type != 'only_winner') ? GuessScoreClient : OneTimeBracketClient;

	const game_type_options = [
		{ k:'result', v:i18n("Exact result") },
		{ k:'margin', v:i18n("Score difference") },
		//{ k:'matches', v:i18n("Match difference") },
	];

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						{(is_bracket === false) && (
							<RichEditorPlaceHolder
								placeholder={default_description_text}
								name="main_description"
								has_emojis={true}
								_class={{ first:true }}
							>Introduction message</RichEditorPlaceHolder>
						)}

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
							//_class={(is_bracket) ? { first:true } : null}
						>Display outer border</Checkbox>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_guess_score_sponsors_image}
							name="guess_score_sponsors_image"
							small_input={true}
							_size={{ width:800, height:300 }}
						>Sponsors image</AutoPostImageUploader>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_bg_image}
							name="bg_image"
							small_input={true}
							_size={{ width:800, height:800 }}
						>Background image</AutoPostImageUploader>

						<ColorPicker
							name='bg_color'
							color={stage?.bg_color ?? default_bg_color}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='text_color'
							color={stage?.text_color ?? default_text_color}
							_class={{ size:50 }}
						>Text color</ColorPicker>

						<ColorPicker
							name='main_color'
							color={stage?.main_color ?? default_main_color}
							_class={{ size:50 }}
						>Action color</ColorPicker>

						{(x2_matches.length > 0) && (
							<ColorPicker
								name='guess_score_x2_color'
								color={stage?.guess_score_x2_color ?? (stage?.text_color ?? default_text_color)}
								_class={{ size:50 }}
							>x2 icon color</ColorPicker>
						)}
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Options"
						icon='knk-settings'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name="guess_score_natural_direction"
							value={stage?.guess_score_natural_direction ?? true}
						>Ascending number scroll</Checkbox>

						<Select
							name="guess_score_type"
							values={game_type_options}
							disabled={(open_stage) ? true : false}
							_class={{ size:50 }}
							after_component={<p>{i18n("You won't be able to edit this option after the stage starts.")}</p>}
						>Prediction type</Select>

						{(type == 'result') && (
							<Range
								min={1}
								max={3}
								step={1}
								name="guess_score_num_digits"
								value={parseInt(stage?.guess_score_num_digits ?? '3')}
								_class={{ size:50 }}
							>Number of digits</Range>
						)}

						{(type == 'margin' && open_stage === false) && (
							<Range
								min={2}
								max={20}
								step={1}
								name="guess_score_step_interval"
								value={step_interval}
								_class={{ size:50 }}
							>Points per interval</Range>
						)}

						{(type == 'margin' && open_stage) && (
							<Input
								name="guess_score_step_interval"
								value={step_interval}
								disabled={true}
								_class={{ size:50 }}
								after_component={<p>{i18n("You won't be able to edit this option after the stage starts.")}</p>}
							>Points per interval</Input>
						)}

						{(type == 'margin') && (
							<Range
								min={3}
								max={10}
								step={1}
								name="guess_score_max_interval"
								value={max_interval}
								_class={{ size:50 }}
							>Total intervals</Range>
						)}
					</Model>

					{(type == 'margin') && (
						<div class="gui_not_in_top">
							<FieldLabel>Intervals defined</FieldLabel>

							<RibbonCollection
								items={defined_intervals}
							/>
						</div>
					)}

					{(type == 'margin') && (
						<Model model={stage} change={change}>
							<Input
								name="guess_score_step_2_instructions"
								max_length={45}
							>Instructions message for score difference selection</Input>

							<Checkbox
								name='guess_score_tint_winner'
								value={stage?.guess_score_tint_winner ?? true}
							>Apply color effect to the winning team</Checkbox>
						</Model>
					)}
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{ width:'100%' }}>
						<ClientComponent
							stage={{ Stage:stage }}
							type={props.type}
							promotion={{ Promotion:props.promotion }}
							is_builder={true}
							is_playable={true}
							section="design"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameBracketDesign = ({ stage, is_bracket, change, ...props }) => {
	const total_rounds = parseInt(stage?.guess_score_num_rounds ?? '4');

	const default_bg_color = props.promotion?.layout_props?.color?.background_color ?? 'rgba(255, 255, 255, 1)';
	const default_text_color = props.promotion?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const default_main_color = props.promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';

	const x2_matches = (stage?.guess_score_matches ?? []).filter((m) => (ensureBoolean(m?.x2 ?? false)));

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
							_class={{ first:true }}
						>Display outer border</Checkbox>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Bracket"
						icon='knk-brackets'
					/>

					<Model model={stage} change={change}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_guess_score_bracket_bg_image}
							name="guess_score_bracket_bg_image"
							small_input={true}
							_size={{ width:800, height:800 }}
							label_props={{
								_extra_component:(isDesktop()) ? <ActionLink
									_href={`/img/bracket_templates/bracket_${total_rounds}_rounds_template.jpg`}
									_target='_blank'
								>Download template</ActionLink> : null,
							}}
						>Background image</AutoPostImageUploader>

						<ColorPicker
							name='guess_score_bracket_bg_color'
							color={stage?.guess_score_bracket_bg_color ?? (darkenMainColorByPercent(0.2, stage?.bg_color ?? default_bg_color))}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<Checkbox
							name='guess_score_tint_winner'
							value={stage?.guess_score_tint_winner ?? true}
						>Apply color effect to the winning team</Checkbox>

						<Range
							min={0}
							max={50}
							step={5}
							name="guess_score_team_border_radius"
							value={parseInt(stage?.guess_score_team_border_radius ?? '0')}
							_class={{ size:50 }}
						>Border radius</Range>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Winning team"
						icon='knk-settings'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name='guess_score_winner_bg_color'
							color={stage?.guess_score_winner_bg_color ?? `rgba(${colorToArray(stage?.main_color ?? default_main_color).join(",")}, 0.2)`}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='guess_score_winner_line_color'
							color={stage?.guess_score_winner_line_color ?? (stage?.main_color ?? default_main_color)}
							_class={{ size:50 }}
						>Line color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Losing team"
						icon='knk-settings'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name='guess_score_loser_bg_color'
							color={stage?.guess_score_loser_bg_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.2)`}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='guess_score_loser_line_color'
							color={stage?.guess_score_loser_line_color ?? (stage?.text_color ?? default_text_color)}
							_class={{ size:50 }}
						>Line color</ColorPicker>

						<Checkbox
							name='guess_score_loser_grayscale'
							value={stage?.guess_score_loser_grayscale ?? true}
						>Show image in gray</Checkbox>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Pending match"
						icon='knk-settings'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name='guess_score_pending_bg_color'
							color={stage?.guess_score_pending_bg_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.1)`}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='guess_score_pending_line_color'
							color={stage?.guess_score_pending_line_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.3)`}
							_class={{ size:50 }}
						>Line color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Winning team selector"
						icon='knk-winners'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name='bg_color'
							color={stage?.bg_color ?? default_bg_color}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='text_color'
							color={stage?.text_color ?? default_text_color}
							_class={{ size:50 }}
						>Text color</ColorPicker>

						<ColorPicker
							name='main_color'
							color={stage?.main_color ?? default_main_color}
							_class={{ size:50 }}
						>Action color</ColorPicker>

						{(x2_matches.length > 0) && (
							<ColorPicker
								name='guess_score_x2_color'
								color={stage?.guess_score_x2_color ?? (stage?.text_color ?? default_text_color)}
								_class={{ size:50 }}
							>x2 icon color</ColorPicker>
						)}

					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{ width:'100%' }}>
						<OneTimeBracketClient
							stage={{ Stage:stage }}
							type={props.type}
							promotion={{ Promotion:props.promotion }}
							is_builder={true}
							is_playable={true}
							section="design"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameSummary = ({ stage, is_bracket, change, teams, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_description_text = `<h1>${i18n("Your results")}</h1><p>${i18n("Click on a score to edit it.")}</p>`;

	const type = stage?.guess_score_type ?? 'result';
	const matches = stage?.guess_score_matches ?? [];

	const page_enabled = (matches.length > 1) ? true : false;

	const ClientComponent = (type != 'only_winner') ? GuessScoreClient : OneTimeBracketClient;

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				{(page_enabled) && (
					<article>
						<Model model={stage} change={change}>
							<RichEditorPlaceHolder
								placeholder={default_description_text}
								name="guess_resume_message"
								has_emojis={true}
								_class={{ first:true }}
							>Introduction message</RichEditorPlaceHolder>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			{(page_enabled) && (
				<BuilderCanvas fit_to_window="content">
					<Layout
						promo={props.promotion}
						is_builder={true}
						page="quiz"
						menus={props.promoModel.Custom__Menus}
						layout_extra_classes="is_new_game"
					>
						<div style={{ width:'100%' }}>
							<ClientComponent
								stage={{ Stage:stage }}
								type={props.type}
								promotion={{ Promotion:props.promotion }}
								is_bracket={is_bracket}
								is_builder={true}
								is_playable={true}
								section="resume"
							/>
						</div>
					</Layout>
				</BuilderCanvas>
			)}

			{(page_enabled === false) && (
				<BuilderCanvas
					fit_to_window="content"
					not_bordered={true}
				>
					<Message
						title={i18n("The summary page will not be shown if there is only one match.")}
						_class={{ other:'in_top in_bottom' }}
					>
						<p>{i18n("If there are multiple matches, the user will see all their predictions on a single screen to review, edit if needed, and confirm.")}</p>
					</Message>
				</BuilderCanvas>
			)}
		</>
	)
};

const GameFinal = ({ stage, is_bracket, change, teams, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type = stage?.guess_score_type ?? 'result';

	const matches = stage?.guess_score_matches ?? [];
	const total_matches = matches.length;

	const default_description_text = `<h1>${i18n("Thank you")}</h1><p>${i18n("Here are the statistics for each match.")}</p>`;

	const ClientComponent = (type != 'only_winner') ? GuessScoreClient : OneTimeBracketClient;

	let stats_count_options = [{ k:'all', v:i18n("All options") }];
	if(total_matches > 10) stats_count_options.unshift({ k:'10', v:i18n("Top 10") });
	if(total_matches > 5) stats_count_options.unshift({ k:'5', v:i18n("Top 5") });
	if(total_matches > 3) stats_count_options.unshift({ k:'3', v:i18n("Top 3") });

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<RichEditorPlaceHolder
							placeholder={default_description_text}
							name="final_message"
							has_emojis={true}
							_class={{ first:true }}
						>Introduction message</RichEditorPlaceHolder>
					</Model>
				</article>

				{(is_bracket === false) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Statistics"
							icon='knk-stats'
						/>

						<Model model={stage} change={change}>
							<Checkbox
								name='guess_display_stats'
								value={stage?.guess_display_stats ?? true}
							>Display statistics</Checkbox>
						</Model>

						{(stage?.guess_display_stats ?? true) && (
							<Model model={stage} change={change}>
								<Input
									name='guess_label_local_wins'
									placeholder="Local wins"
									_class={{ size:50 }}
								>Local wins label</Input>

								<ColorPicker
									name='guess_color_local_wins'
									color={stage?.guess_color_local_wins ?? 'rgba(120, 195, 0, 1)'}
									_class={{ size:50 }}
								>Local wins color</ColorPicker>

								{(type == 'result') && (
									<Input
										name='guess_label_draw'
										placeholder={i18n("***Draw")}
										autotranslate_placeholder={false}
										_class={{ size:50 }}
									>Draw label</Input>
								)}

								{(type == 'result') && (
									<ColorPicker
										name='guess_color_draw'
										color={stage?.guess_color_draw ?? 'rgba(255, 152, 0, 1)'}
										_class={{ size:50 }}
									>Draw color</ColorPicker>
								)}

								<Input
									name='guess_label_visitor_wins'
									placeholder="Visitor wins"
									_class={{ size:50 }}
								>Visitor wins label</Input>

								<ColorPicker
									name='guess_color_visitor_wins'
									color={stage?.guess_color_visitor_wins ?? 'rgba(215, 35, 60, 1)'}
									_class={{ size:50 }}
								>Visitor wins color</ColorPicker>
							</Model>
						)}
					</article>
				)}

				{(is_bracket) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Chart with results"
							icon='knk-stats'
						/>

						<Model model={stage} change={change}>
							<Checkbox
								name="guess_score_display_stats"
								value={stage?.guess_score_display_stats ?? true}
							>Show the chart with results</Checkbox>

							<Checkbox
								name="guess_score_display_abs_stats"
								value={stage?.guess_score_display_abs_stats ?? false}
							>Show the total number of votes per option</Checkbox>

							<Select
								name="guess_score_stats_count"
								values={stats_count_options}
								_class={{ size:60 }}
							>Options to display</Select>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{ width:'100%' }}>
						<ClientComponent
							stage={{ Stage:stage }}
							type={props.type}
							promotion={{ Promotion:props.promotion }}
							is_bracket={is_bracket}
							is_builder={true}
							is_playable={true}
							section="score"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameResults = ({ stage, is_bracket, change, teams, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ edit_mode, setEditMode ] = useState(false);
	const [ highlighted, setHighlighted ] = useState(false);
	const [ disabled_matches, setDisabledMatches ] = useState([]);

	const promotion = props.promotion;
	const end_date = (isMultiStage(promotion)) ? stage.end_date : promotion.end_entry;
	const closed_stage = EPDateAfter(null, end_date);

	const matches = stage?.guess_score_matches ?? [];
	const rounds = stage?.guess_score_rounds ?? [];
	const total_rounds = (is_bracket) ? parseInt(stage?.guess_score_num_rounds ?? '4') : 1;
	const matches_results = stage?.guess_score_matches_result ?? {};
	const type = stage?.guess_score_type ?? 'result';
	const max_value = (type == 'result') ? Math.pow(10, parseInt(stage?.guess_score_num_digits ?? '1')) - 1 : 999;//margin max 999

	useEffect(() => {
		if(is_bracket === false) return;
		if(edit_mode === false) return;

		const new_disabled_matches = [];
		let previous_rounds_matches = 0;

		[...Array(total_rounds).keys()].map((round) => {
			const num_matches_in_round = Math.pow(2, total_rounds - round) / 2;
			if(round == 0) {
				previous_rounds_matches += num_matches_in_round;
				return;
			}

			[...Array(num_matches_in_round).keys()].map((match_index) => {
				const has_winner = matches_results[matches[previous_rounds_matches + match_index].id]?.winner ?? false;
				if(has_winner === false) return;

				const match_key_1 = `${round - 1}_${match_index * 2}`;
				const match_key_2 = `${round - 1}_${match_index * 2 + 1}`

				if(!new_disabled_matches.includes(match_key_1)) new_disabled_matches.push(match_key_1);
				if(!new_disabled_matches.includes(match_key_2)) new_disabled_matches.push(match_key_2);
			});

			previous_rounds_matches += num_matches_in_round;
		});

		setDisabledMatches(new_disabled_matches);
	}, [ edit_mode ]);

	const findTeamById = (id) => {
		return teams.find((t) => (t.id == id));
	};

	const changeResult = (k, v, match_id) => {
		if(edit_mode === false) return;

		if(matches_results[match_id] == undefined) matches_results[match_id] = { id_1:'', id_2:'' };
		matches_results[match_id][k] = (v != '') ? v : 0;

		change('guess_score_matches_result', matches_results);
	};

	const onBlurResult = (match_id) => {
		setHighlighted(false);
		if(type != 'margin') return;
		const result_id_1 = matches_results?.[match_id]?.id_1 ?? '';
		const result_id_2 = matches_results?.[match_id]?.id_2 ?? '';
		if(result_id_1 == '' || result_id_2 == '') return;
		if(parseInt(result_id_1) != parseInt(result_id_2)) return;
		setHighlighted(match_id);
	};

	const generateFutureMatches = (match_index, team_id) => {
		const winner_next_match = Math.ceil((match_index + 1) / 2) + Math.pow(2, total_rounds) / 2 - 1;
		const winner_next_match_pos = (Math.ceil((match_index + 1) / 2) == (match_index + 1) / 2) ? 2 : 1;

		matches[winner_next_match][`id_${winner_next_match_pos}`] = team_id;
	};

	const defineWinner = (match_id, team_index, round_index, match_index) => {
		if(edit_mode === false) return;
		if(is_bracket === false) return;

		if(matches_results[match_id] == undefined) matches_results[match_id] = { winner:'', id_1:'', id_2:'' };
		matches_results[match_id].winner = team_index;

		if(round_index > 0) {
			const new_disabled_matches = [ ...disabled_matches ];

			const match_key_1 = `${round_index - 1}_${match_index * 2}`;
			const match_key_2 = `${round_index - 1}_${match_index * 2 + 1}`;
			if(!new_disabled_matches.includes(match_key_1)) new_disabled_matches.push(match_key_1);
			if(!new_disabled_matches.includes(match_key_2)) new_disabled_matches.push(match_key_2);

			setDisabledMatches(new_disabled_matches);
		}

		if(round_index < total_rounds - 1) {
			let previous_rounds_matches = 0;
			[...Array(round_index).keys()].map((r) => {
				previous_rounds_matches += Math.pow(2, total_rounds - r) / 2;
			});

			const real_match_index = match_index + previous_rounds_matches;

			generateFutureMatches(real_match_index, matches[real_match_index][`id_${team_index}`]);
		}

		change('guess_score_matches_result', matches_results);
	};

	const finishEdit = (e, cb) => {
		props.saveStage(() => {
			setEditMode(false);
			if(cb != undefined) cb();
		});
	};

	const initEdit = () => {
		setEditMode(true);
	};

	const resetResults = (e, cb) => {
		if(is_bracket === false) return;

		setDisabledMatches([]);

		const new_matches = [];
		matches.map((match, m) => {
			if(m < Math.pow(2, total_rounds) / 2) {
				new_matches.push(match);
				return;
			}

			new_matches.push({
				...match,
				id_1:'',
				id_2:'',
			});
		});

		change('guess_score_matches', new_matches);
		change('guess_score_matches_result', '');

		props.saveStage(() => {
			if(cb != undefined) cb();
		});
	};

	const grouped_matches = [];
	if(is_bracket === false) {
		grouped_matches.push({
			label:false,
			matches:[...Array(matches.length).keys()],
		});
	} else {
		let previous_index = 0;
		[...Array(total_rounds).keys()].map((round, r) => {
			const num_matches_in_round = Math.pow(2, (total_rounds - r - 1));

			const matches_in_round = [];
			for(let i = 0; i < num_matches_in_round; i++) {
				matches_in_round.push(i + previous_index);
			}

			grouped_matches.push({
				label:`${i18n("Round", 'uppercase')} ${r + 1}${(isNotEmpty(rounds[r], 'title') ? `: ${rounds[r].title}` : '')}`,
				matches:matches_in_round,
			});
			previous_index += num_matches_in_round;
		});
	}

	const fm = getFilteredMatches(matches);

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					{(is_bracket === false) && (
						<>
							<Model
								model={stage}
								change={change}
							>
								<Input
									name="guess_score_points_result"
									value={stage?.guess_score_points_result ?? 0}
									min={0}
									max={100}
									_type='number'
									_class={{ size:30, first:true }}
								>{(type == 'margin') ? "Points for correct difference interval" : "Points for exact result"}</Input>
							</Model>

							<CheckEnabler
								label={(type == 'margin') ? "Points for predicting the winning team" : "Points for predicting winner or draw"}
								enabled={(stage?.guess_score_points_outcome ?? false) ? true : false}
								onDisable={() => change('guess_score_points_outcome', null)}
								onEnable={() => change('guess_score_points_outcome', 1)}
							>
								<Input
									name="guess_score_points_outcome"
									value={stage?.guess_score_points_outcome ?? 1}
									min={0}
									max={100}
									_type='number'
									_class={{ size:30 }}
									model={stage}
									change={change}
								>Points for predicting winner or draw</Input>
							</CheckEnabler>
						</>
					)}

					{(is_bracket) && (
						<>
							<Model
								model={stage}
								change={change}
							>
								<Input
									name="guess_score_points_outcome"
									value={stage?.guess_score_points_outcome ?? 0}
									min={0}
									max={100}
									_type='number'
									_class={{ size:30, first:true }}
								>Points for correctly predicting the advancing team</Input>
							</Model>

							<InformationMessage
								icon={false}
							>
								<p>{i18n("Points are awarded for each correct pick. The score is multiplied by the round: x1 in round 1, x2 in round 2, x3 in round 3 and x4 in round 4.")}</p>
							</InformationMessage>
						</>
					)}

					<StageScoreRecalculate
						previous_cb = {props.saveStage}
						promotion={promotion}
						stage={stage}
						intl={props.intl}
						disabled={(!closed_stage || edit_mode) ? true : false}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				not_awesome={true}
				not_bordered={true}
				off_content={<ButtonCollection>
					{(edit_mode === false) ? (
						<ActionButton
							icon='knk-edit'
							onClick={initEdit}
						>Edit results</ActionButton>
					) : (
						<SaveButton
							icon='knk-off'
							onClick={finishEdit}
						>Save results</SaveButton>
					)}

					{(is_bracket && Object.keys(matches_results).length > 0) && (
						<DeleteButton
							onClick={resetResults}
						>Start over</DeleteButton>
					)}
				</ButtonCollection>}
			>
				<article class="matches">
					{grouped_matches.map((round, r) => {
						let match_index = 0;

						return (
							<div key={`round_${r}`}>
								{(isNotEmpty(round, 'label')) && (
									<div class="round_label">
										<span class="t">{round.label}</span>
									</div>
								)}

								<div class="matches_list">
									{round.matches.map((_match, m) => {
										const match = matches[_match];

										if(is_bracket === false) {
											const is_complete = fm.find((mi) => (mi.id == match.id));
											if(is_complete == undefined || !is_complete) return null;
										}

										match_index++;

										const team_1 = findTeamById(match.id_1);
										const team_2 = findTeamById(match.id_2);

										const result = matches_results[match.id];
										const default_props = {
											result_index:m,
											promotion_id:props.promotion_id,
											intl:props.intl,
										};

										const classes = ['match_pairing_c'];
										if(highlighted == match.id) classes.push('is_highlight');

										const disabled_match = (disabled_matches.includes(`${r}_${m}`)) ? true : false;

										return (
											<div key={`match_pair_${match.id}`} class={classes.join(" ")}>
												<span>{i18n("Match")} #{match_index}:</span>

												<div class="match_pairing">
													<TeamSlot
														{...default_props}
														team={team_1}
														team_index={0}
														type='result'
													>
														{(type == 'only_winner') && (
															<>
																{(edit_mode && team_1 != undefined && team_2 != undefined) ? (
																	<ToggleButton
																		active={(result?.winner == '1') ? true : false}
																		secondary={(result?.winner == '1') ? false : true}
																		disabled={disabled_match}
																		onClick={(k, v) => defineWinner(match.id, '1', r, m)}
																		_class={{ first:true }}
																		tooltip={(disabled_match) ? <Tooltip
																			with_icon={false}
																			position='top'
																		>The winner of this match has already played the following match, so it can't be edited. You can start over to change the winner of this match, if you need to.</Tooltip> : null}
																	>Winner</ToggleButton>
																) : (
																	<div class="fixed_result common_form_item">
																		{((result?.winner ?? '') == '1') && (
																			<span class="result_icon match_winner ep-winners" />
																		)}

																		{((result?.winner ?? '') == '2') && (
																			<span class="result_icon match_loser knk-white-space" />
																		)}

																		{((result?.winner ?? '') == '') && (
																			"–"
																		)}
																	</div>
																)}
															</>
														)}

														{(type != 'only_winner') && (
															<>
																{(edit_mode) ? (
																	<Input
																		name='id_1'
																		value={parseInt(result?.id_1 ?? '')}
																		min={0}
																		max={max_value}
																		_type='number'
																		_class={{ first:true }}
																		onBlur={() => onBlurResult(match.id)}
																		change={(k, v) => changeResult(k, v, match.id)}
																	/>
																) : (
																	<div class="fixed_result common_form_item">{result?.id_1 ?? "–"}</div>
																)}
															</>
														)}
														
													</TeamSlot>

													<span class="vs">{i18n("vs")}</span>

													<TeamSlot
														{...default_props}
														team={team_2}
														team_index={1}
														type='result'
													>
														{(type == 'only_winner') && (
															<>
																{(edit_mode && team_1 != undefined && team_2 != undefined) ? (
																	<ToggleButton
																		active={(result?.winner == '2') ? true : false}
																		secondary={(result?.winner == '2') ? false : true}
																		disabled={disabled_match}
																		onClick={(k, v) => defineWinner(match.id, '2', r, m)}
																		_class={{ first:true }}
																		tooltip={(disabled_match) ? <Tooltip
																			with_icon={false}
																			position='top'
																		>The winner of this match has already played the following match, so it can't be edited. You can start over to change the winner of this match, if you need to.</Tooltip> : null}
																	>Winner</ToggleButton>
																) : (
																	<div class="fixed_result common_form_item">
																		{((result?.winner ?? '') == '2') && (
																			<span class="result_icon match_winner ep-winners" />
																		)}

																		{((result?.winner ?? '') == '1') && (
																			<span class="result_icon match_loser knk-white-space" />
																		)}

																		{((result?.winner ?? '') == '') && (
																			"–"
																		)}
																	</div>
																)}
															</>
														)}

														{(type != 'only_winner') && (
															<>
																{(edit_mode) ? (
																	<Input
																		name='id_2'
																		value={parseInt(result?.id_2 ?? '')}
																		min={0}
																		max={max_value}
																		_type='number'
																		_class={{ first:true }}
																		onBlur={() => onBlurResult(match.id)}
																		change={(k, v) => changeResult(k, v, match.id)}
																	/>
																) : (
																	<div class="fixed_result common_form_item">{result?.id_2 ?? "–"}</div>
																)}
															</>
														)}
													</TeamSlot>
												</div>
											</div>
										)
									})}
								</div>
							</div>
						)
					})}
				</article>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, is_bracket, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	const ClientComponent = (is_bracket === false) ? GuessScoreClient : OneTimeBracketClient;

	return (
		<>
			<BuilderSidebar>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{ width:'100%' }}>
						<ClientComponent
							stage={{ Stage:stage }}
							type={props.type}
							promotion={{ Promotion:props.promotion }}
							is_bracket={is_bracket}
							is_builder={true}
							is_playable={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GuessScoreBuilder = ({ base_route, ...props }) => {
	const is_bracket = props?.is_bracket ?? false;

	const type = props?.type ?? ((is_bracket === false) ? 'score_predictor' : 'bracket_predictor');

	const basename = `/stages/${props.stage.id}/${type}`;
	const stage = props.stage;

	const matches = getFilteredMatches(stage?.guess_score_matches ?? []);

	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Matches", link:`${basename}/matches`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Summary page", link:`${basename}/summary`, icon:'knk-like', enabled:(is_bracket === false) ? true : false },
		{ label:"Final screen", link:`${basename}/final`, icon:'knk-stats' },
		{ label:"Results", link:`${basename}/results`, icon:'knk-goal', disabled:(matches.length < 1) ? true : false },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok', disabled:(matches.length < 1) ? true : false },
	];
	const [teams,setTeams] = useState(props.promotion?.guess_score_teams ?? []);

	const common_props = {
		stage,
		type,
		teams,
		is_bracket,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		promoChangeSave:props.promoChangeSave,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
		setTeams,
	};

	const DesignComponent = (is_bracket) ? GameBracketDesign : GameDesign;

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/matches">
						<GameMatches
							{...common_props}
							nav_active={`/matches`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<DesignComponent
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/summary">
						<GameSummary
							{...common_props}
							nav_active={`/summary`}
						/>
					</Route>

					<Route exact={true} path="/final">
						<GameFinal
							{...common_props}
							nav_active={`/final`}
						/>
					</Route>

					<Route exact={true} path="/results">
						<GameResults
							{...common_props}
							nav_active={`/results`}
						/>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(GuessScoreBuilder);